import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ChallengeCard = ({ challenge, onParticipate }) => {
  const navigate = useNavigate();

  const getTimeRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diff = end - now;
    
    if (diff <= 0) return 'Ended';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const getProgressPercentage = () => {
    return Math.min((challenge?.currentAmount / challenge?.goalAmount) * 100, 100);
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-elevation-1 hover:shadow-elevation-2 transition-gentle">
      {/* Challenge Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={challenge?.image}
          alt={challenge?.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
            challenge?.status === 'active' ?'bg-success text-success-foreground' 
              : challenge?.status === 'ending-soon' ?'bg-warning text-warning-foreground' :'bg-muted text-muted-foreground'
          }`}>
            {challenge?.status === 'active' ? 'Active' : 
             challenge?.status === 'ending-soon' ? 'Ending Soon' : 'Completed'}
          </span>
        </div>
        <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-lg">
          <div className="flex items-center space-x-1 text-xs font-medium">
            <Icon name="Clock" size={12} />
            <span>{getTimeRemaining(challenge?.endDate)}</span>
          </div>
        </div>
      </div>
      {/* Challenge Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-card-foreground mb-1 line-clamp-2">
              {challenge?.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              {challenge?.category}
            </p>
          </div>
          <div className="flex items-center space-x-1 text-sm text-muted-foreground ml-4">
            <Icon name="Users" size={14} />
            <span>{challenge?.participantCount}</span>
          </div>
        </div>

        <p className="text-sm text-card-foreground mb-4 line-clamp-2">
          {challenge?.description}
        </p>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-card-foreground">
              {formatAmount(challenge?.currentAmount)} raised
            </span>
            <span className="text-sm text-muted-foreground">
              Goal: {formatAmount(challenge?.goalAmount)}
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {Math.round(getProgressPercentage())}% complete
          </div>
        </div>

        {/* Top Contributors Preview */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-card-foreground">Top Contributors</span>
            <button 
              onClick={() => navigate(`/giving-challenges/${challenge?.id}/leaderboard`)}
              className="text-xs text-primary hover:text-primary/80 transition-micro"
            >
              View all
            </button>
          </div>
          <div className="flex items-center space-x-2">
            {challenge?.topContributors?.slice(0, 3)?.map((contributor, index) => (
              <div key={contributor?.id} className="flex items-center space-x-1">
                <div className="w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xs font-medium">
                  {contributor?.name?.charAt(0)}
                </div>
                {index === 0 && (
                  <Icon name="Crown" size={12} className="text-warning" />
                )}
              </div>
            ))}
            {challenge?.participantCount > 3 && (
              <span className="text-xs text-muted-foreground">
                +{challenge?.participantCount - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            onClick={() => onParticipate(challenge)}
            disabled={challenge?.status === 'completed'}
            className="flex-1"
            iconName="Heart"
            iconPosition="left"
          >
            {challenge?.isParticipating ? 'Donate More' : 'Join Challenge'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/giving-challenges/${challenge?.id}`)}
            iconName="ExternalLink"
            iconPosition="right"
          >
            Details
          </Button>
        </div>

        {/* Challenge Badges */}
        {challenge?.badges && challenge?.badges?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {challenge?.badges?.map((badge, index) => (
              <span 
                key={index}
                className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full"
              >
                {badge}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengeCard;